import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as fragments from '~/apollo/fragments';
import type {
  StudyReservoirModelsRouteQuery,
  StudyReservoirModelsRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { useStudyOutletContext } from '~/routes/study/$studyId';

const STUDY_RESERVOIR_MODELS_ROUTE = gql`
  query StudyReservoirModelsRoute($studyId: Int!) {
    studyList(id: $studyId) {
      ...studyParts
      reservoirModels {
        ...reservoirModelParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
          }
        }
        files {
          ...fileParts
        }
      }
    }
  }

  ${fragments.studyParts}
  ${fragments.reservoirModelParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
`;

export default function StudyReservoirModelsRoute() {
  const ctx = useStudyOutletContext();
  const studyId = ctx.study.id;

  const { data, loading } = useQuery<
    StudyReservoirModelsRouteQuery,
    StudyReservoirModelsRouteQueryVariables
  >(STUDY_RESERVOIR_MODELS_ROUTE, { variables: { studyId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: STUDY_RESERVOIR_MODELS_ROUTE, variables: { studyId } },
  ];

  const items =
    data?.studyList.find(s => s.id === studyId)?.reservoirModels ?? [];

  if (loading) return <SpinnerPlaceholder />;

  return (
    <SupportingObjectList
      items={items}
      soParentType="study"
      soParentId={studyId}
      bookmarkParentType={BookmarkParentType.ReservoirModel}
      refetchQueries={refetchQueries}
    />
  );
}
